import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import logo from '../assets/logo.png';

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (isMobileMenuOpen) closeMobileMenu();
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]);

  const openMobileMenu = () => {
    setIsMobileMenuOpen(true);
    // slight delay so the element is mounted before animating in
    requestAnimationFrame(() => setIsMenuVisible(true));
  };

  const closeMobileMenu = () => {
    setIsMenuVisible(false);
    // wait for animation to finish before unmounting
    setTimeout(() => setIsMobileMenuOpen(false), 300);
  };

  const toggleMobileMenu = () => {
    isMobileMenuOpen ? closeMobileMenu() : openMobileMenu();
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      closeMobileMenu();
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent py-2 px-4 xl:px-2">
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        style={{
          backgroundColor: isScrolled ? 'white' : 'transparent',
          boxShadow: isScrolled ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease, padding 0.3s ease',
          borderRadius: 20,
        }}
      >
        <div className="flex justify-between items-center">
          <button
            onClick={() => scrollToSection('hero')}
            className={`text-2xl font-bold transition-colors ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
          >
            <img src={logo} alt="Xemtio Logo" className="h-20 w-auto" />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-8">
            {['Usluge', 'Galerija', 'O nama', 'Kontakt'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                className={`transition-colors ${
                  isScrolled
                    ? 'text-gray-700 hover:text-gray-900'
                    : 'text-white hover:text-gray-200'
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? (
              <X className={isScrolled ? 'text-gray-900' : 'text-white'} />
            ) : (
              <Menu className={isScrolled ? 'text-gray-900' : 'text-white'} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden mt-4 overflow-hidden"
            style={{
              opacity: isMenuVisible ? 1 : 0,
              maxHeight: isMenuVisible ? '400px' : '0px',
              transform: isMenuVisible ? 'translateY(0)' : 'translateY(-8px)',
              transition: 'opacity 0.3s ease, transform 0.3s ease, max-height 0.3s ease',
            }}
          >
            <div className="bg-white rounded-lg py-4">
              {['Usluge', 'Galerija', 'O nama', 'Kontakt'].map((item, index) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                  className="block w-full text-center text-xl px-4 py-3 text-gray-700 hover:bg-gray-50"
                  style={{
                    opacity: isMenuVisible ? 1 : 0,
                    transform: isMenuVisible ? 'translateY(0)' : 'translateY(-6px)',
                    transition: `opacity 0.3s ease ${index * 50}ms, transform 0.3s ease ${index * 50}ms`,
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}